import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { notifySuccess } from "../../../../components/AppToast";
import { fieldError, staffApiError } from "../apiErrors";
import { useUpdateStaffRoles } from "../hooks";
import { PLATFORM_ADMIN } from "../../permissions";
import { StaffRolesSelect, optionsToRoles, rolesToOptions } from "./StaffRolesSelect";

const HEADING_ID = "edit-staff-roles-modal-title";
const FORM_ID = "edit-staff-roles-form";

/**
 * Cambio de roles de un miembro (PLATFORM_ADMIN).
 *
 * El PATCH **reemplaza** la lista completa, no hace merge: por eso el select
 * llega precargado con los roles actuales y el aviso es explícito. Quitarle el
 * rol al último PLATFORM_ADMIN activo devuelve 409 `staff_last_platform_admin`.
 */
function EditStaffRolesModal({ member, isLastAdmin, onHide }) {
  const [roles, setRoles] = useState(() => rolesToOptions(member?.staff_roles));
  const [rolesError, setRolesError] = useState(null);
  const updateRoles = useUpdateStaffRoles();

  useEffect(() => {
    setRoles(rolesToOptions(member?.staff_roles));
    setRolesError(null);
  }, [member]);

  function handleSubmit(e) {
    e.preventDefault();
    if (roles.length === 0) {
      setRolesError("Selecciona al menos un rol.");
      return;
    }
    // Quitarle el rol al último administrador activo deja la plataforma sin
    // nadie que pueda gestionarla: el backend lo rechaza con 409
    // `staff_last_platform_admin`. Se avisa aquí, antes de enviar. No se
    // deshabilita el botón entero porque SÍ es válido editarle otros roles
    // mientras conserve PLATFORM_ADMIN.
    const next = optionsToRoles(roles);
    if (isLastAdmin && !next.includes(PLATFORM_ADMIN)) {
      setRolesError(
        "Es el único administrador de plataforma activo: no puedes quitarle ese rol. Asígnaselo antes a otra persona."
      );
      return;
    }
    setRolesError(null);
    updateRoles.mutate(
      { id: member.id, roles: next },
      {
        onSuccess: () => {
          notifySuccess(`Roles actualizados — ${member.email}.`);
          onHide();
        },
      }
    );
  }

  return (
    <Modal
      show={Boolean(member)}
      onHide={onHide}
      aria-labelledby={HEADING_ID}
      aria-modal="true"
    >
      <Modal.Header closeButton>
        <Modal.Title id={HEADING_ID}>Editar roles</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {updateRoles.isError && (
          <Alert variant="danger" className="small">
            {staffApiError(updateRoles.error, "No se pudieron actualizar los roles.")}
          </Alert>
        )}
        <p className="mb-3">
          <strong>{member?.name}</strong>
          <br />
          <span className="text-muted small">{member?.email}</span>
        </p>
        <Form id={FORM_ID} onSubmit={handleSubmit}>
          <Form.Group controlId="staff-edit-roles">
            <Form.Label>Roles</Form.Label>
            <StaffRolesSelect
              inputId="staff-edit-roles"
              value={roles}
              onChange={setRoles}
              isDisabled={updateRoles.isPending}
            />
            {(rolesError || fieldError(updateRoles.error, "roles")) && (
              <Form.Text className="text-danger">
                {rolesError ?? fieldError(updateRoles.error, "roles")}
              </Form.Text>
            )}
          </Form.Group>
        </Form>
        <Alert variant="warning" className="small mt-3 mb-0">
          Los roles seleccionados <strong>reemplazan</strong> por completo los
          actuales.
          {isLastAdmin && (
            <>
              {" "}
              Es el <strong>único administrador de plataforma activo</strong>:
              debe conservar ese rol.
            </>
          )}
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={updateRoles.isPending}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          form={FORM_ID}
          disabled={updateRoles.isPending}
          aria-label="Guardar roles del miembro de staff"
        >
          {updateRoles.isPending ? <Spinner size="sm" aria-hidden="true" /> : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export { EditStaffRolesModal };
